import type { MetadataDto } from '../dtos/MetadataDto.js';
import type { ProjectMetadataDto } from '../dtos/ProjectMetadataDto.js';
import { getSupabaseClient } from '../../../../core/supabase/client.js';

const FIELDS = `id, org_id, project_id, type, name, description, created_at, updated_at`;
const PM_FIELDS = `project_id, metadata_id, display_name, created_at, updated_at`;

export class MetadataRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async getById(id: string): Promise<MetadataDto | null> {
    const { data, error } = await this.supabase.from('metadatas').select(FIELDS).eq('id', id).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async list(options: { orgId: string; type?: string; projectId?: string; limit?: number }): Promise<MetadataDto[]> {
    let query = this.supabase.from('metadatas').select(FIELDS).eq('org_id', options.orgId).order('type').order('name');
    if (options.type) query = query.eq('type', options.type);
    if (options.projectId) query = query.eq('project_id', options.projectId);
    if (options.limit) query = query.limit(options.limit);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async create(payload: Omit<MetadataDto, 'id' | 'created_at' | 'updated_at'>): Promise<MetadataDto | null> {
    const { data, error } = await this.supabase.from('metadatas').insert(payload).select(FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async update(id: string, payload: Partial<MetadataDto>): Promise<MetadataDto | null> {
    const { data, error } = await this.supabase.from('metadatas').update(payload).eq('id', id).select(FIELDS).maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('metadatas').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }

  async searchMetadatas(options: { orgId: string; query: string; type?: string; limit?: number }): Promise<MetadataDto[]> {
    const { data, error } = await this.supabase.rpc('search_metadatas', {
      p_org_id: options.orgId,
      search_query: options.query,
      row_limit: options.limit ?? 20,
      row_offset: 0,
    });
    if (error) throw new Error(error.message);
    type SearchRow = {
      id: string;
      org_id?: string;
      project_id?: string | null;
      type: string;
      name: string;
      description?: string | null;
      created_at?: string;
      updated_at?: string;
    };
    const rows = (data ?? []) as SearchRow[];
    const filtered = options.type ? rows.filter((m) => m.type === options.type) : rows;
    return filtered.map((m) => ({
      id: m.id,
      org_id: m.org_id ?? options.orgId,
      project_id: m.project_id ?? null,
      type: m.type,
      name: m.name,
      description: m.description ?? null,
      created_at: m.created_at ?? '',
      updated_at: m.updated_at ?? '',
    }));
  }

  async listProjectAdoptions(projectId: string): Promise<ProjectMetadataDto[]> {
    const { data, error } = await this.supabase
      .from('project_metadatas')
      .select(PM_FIELDS)
      .eq('project_id', projectId);
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async adopt(projectId: string, metadataId: string): Promise<void> {
    const { error } = await this.supabase
      .from('project_metadatas')
      .insert({ project_id: projectId, metadata_id: metadataId });
    if (error) throw new Error(error.message);
  }

  async unadopt(projectId: string, metadataId: string): Promise<void> {
    const { error } = await this.supabase
      .from('project_metadatas')
      .delete()
      .eq('project_id', projectId)
      .eq('metadata_id', metadataId);
    if (error) throw new Error(error.message);
  }

  async updateAdoption(projectId: string, metadataId: string, displayName: string | null): Promise<void> {
    const { error } = await this.supabase
      .from('project_metadatas')
      .update({ display_name: displayName })
      .eq('project_id', projectId)
      .eq('metadata_id', metadataId);
    if (error) throw new Error(error.message);
  }
}
